
import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { StorageService } from 'app/core/auth/storage.service';

@Component({
    selector: 'app-header',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200 shadow-sm z-50 relative">
            <!-- Sidebar Toggle Button -->
             <button class="p-2 -ml-2 rounded-lg text-gray-600 hover:bg-gray-100" (click)="toggleSidebar.emit()">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>
            </button>

            <!-- Title / Breadcrumb (Hidden on Mobile if needed) -->
            <div class="text-lg font-semibold text-gray-800 hidden md:block">
            </div>

            <!-- Right Side Actions -->
            <div class="flex items-center gap-4">
                <!-- Outlet Switcher -->
                <div class="relative z-[100]" *ngIf="outlets.length > 1">
                    <button 
                        (click)="toggleOutletDropdown()"
                        class="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 text-primary-green hover:bg-green-100 transition">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                        </svg>
                        <span class="font-medium text-sm hidden sm:inline">{{ currentOutlet?.name || 'Pilih Outlet' }}</span>
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                    </button>

                    <!-- Backdrop -->
                    <div *ngIf="isOutletDropdownOpen" 
                        (click)="isOutletDropdownOpen = false" 
                        class="fixed inset-0 z-[98]">
                    </div>

                    <!-- Dropdown -->
                    <div *ngIf="isOutletDropdownOpen" 
                        class="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[999]">
                        <div class="px-3 py-2 text-xs font-semibold text-gray-400 uppercase">Pilih Outlet</div>
                        <div *ngFor="let outlet of outlets" 
                            (click)="switchOutlet(outlet)"
                            class="px-3 py-2 hover:bg-green-50 cursor-pointer flex items-center gap-3 transition"
                            [class.bg-green-50]="outlet.id === currentOutlet?.id">
                            <div class="w-8 h-8 bg-primary-green bg-opacity-10 rounded-full flex items-center justify-center">
                                <span class="text-primary-green font-bold text-xs">{{ outlet.code }}</span>
                            </div>
                            <div class="flex-1">
                                <div class="font-medium text-gray-800 text-sm">{{ outlet.name }}</div>
                                <div class="text-xs text-gray-500">{{ outlet.address }}</div>
                            </div>
                            <svg *ngIf="outlet.id === currentOutlet?.id" class="w-5 h-5 text-primary-green" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <!-- Single Outlet Display -->
                <div *ngIf="outlets.length === 1" class="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 text-gray-600">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                    </svg>
                    <span class="font-medium text-sm hidden sm:inline">{{ currentOutlet?.name }}</span>
                </div>

                <div class="h-8 w-px bg-gray-200 mx-2"></div>

                <button (click)="signOut()" class="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-red-500 transition">
                    <span>Sign Out</span>
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
                </button>
            </div>
        </div>

    `
})
export class HeaderComponent implements OnInit {
    @Output() toggleSidebar = new EventEmitter<void>();

    outlets: any[] = [];
    currentOutlet: any = null;
    isOutletDropdownOpen = false;

    constructor(
        private _authService: AuthService, 
        private _router: Router,
        private _storage: StorageService
    ) {}

    ngOnInit(): void {
        this.outlets = this._storage.getOutlets();
        this.currentOutlet = this._storage.getOutlet();
    }

    toggleOutletDropdown(): void {
        this.isOutletDropdownOpen = !this.isOutletDropdownOpen;
    }

    switchOutlet(outlet: any): void {
        this._storage.setOutlet(outlet);
        this.currentOutlet = outlet;
        this.isOutletDropdownOpen = false;
        
        // Reload current page to refresh data with new outlet
        window.location.reload();
    }

    signOut(): void {
        this._authService.signOut().subscribe(() => {
            this._router.navigate(['/sign-in']);
        });
    }
}
